import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faPaperPlane, faCommentDots } from "@fortawesome/free-solid-svg-icons";

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [step, setStep] = useState(0);
  const [userData, setUserData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
  });
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  const addBotMessage = (text, options = []) => {
    setIsTyping(true);
    setTimeout(() => {
      setMessages((prev) => [...prev, { text, sender: "bot", options }]);
      setIsTyping(false);
    }, 1000); // 1s delay for realistic typing
  };

  const addUserMessage = (text) => {
    setMessages((prev) => [...prev, { text, sender: "user" }]);
  };

  // Initialization
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      addBotMessage(
        "Welcome to Jetty Estates! 👋 I'm your digital assistant. Could you please provide your full name so we can assist you better?",
      );
      setStep(1);
    }
  }, [isOpen, messages.length]);

  const handleSend = () => {
    if (!inputValue.trim()) return;

    const text = inputValue.trim();
    addUserMessage(text);
    setInputValue("");

    if (step === 1) {
      // Name provided
      if (text.length < 2) {
        addBotMessage(`Could you please provide a valid name?`);
        return;
      }
      setUserData((prev) => ({ ...prev, name: text }));
      addBotMessage(`Thanks, ${text}! What is the best phone number to reach you at?`);
      setStep(2);
    } else if (step === 2) {
      // Phone provided
      const phoneRegex = /^[\d\s\-+()]{7,20}$/;
      if (!phoneRegex.test(text)) {
        addBotMessage(`That doesn't look like a valid phone number. Please provide a valid number.`);
        return;
      }
      setUserData((prev) => ({ ...prev, phone: text }));
      addBotMessage(`Great. And your email address?`);
      setStep(3);
    } else if (step === 3) {
      // Email provided
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(text)) {
        addBotMessage(`Please provide a valid email address.`);
        return;
      }
      setUserData((prev) => ({ ...prev, email: text }));
      addBotMessage(`Thank you. How can we help you today?`, [
        "Schedule a Site Visit",
        "Speak to an Agent",
        "Learn about Jetty Mayfair",
        "General Inquiry"
      ]);
      setStep(4);
    } else if (step === 5) {
       // Additional details
       setUserData((prev) => ({ ...prev, details: text }));
       submitLead({ ...userData, details: text });
       addBotMessage(`Thank you, ${userData.name}! Our team will get back to you shortly.`);
       setStep(6);
    }
  };

  const handleOptionClick = (option) => {
    addUserMessage(option);
    if (step === 4) {
      setUserData((prev) => ({ ...prev, service: option }));
      addBotMessage(`Got it. Could you provide any additional details or requirements?`);
      setStep(5);
    }
  };

  const submitLead = (data) => {
    console.log("ChatBot Lead Submitted:", data);
    // In a real app, send data to your backend API here
  };

  return (
    <>
      {/* Floating Action Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-20 md:bottom-6 right-4 md:right-6 w-14 h-14 md:w-16 md:h-16 bg-primary text-secondary rounded-full shadow-2xl flex items-center justify-center text-xl md:text-2xl hover:scale-110 transition-transform z-50 border-2 border-secondary"
        >
          <FontAwesomeIcon icon={faCommentDots} />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-20 md:bottom-6 right-4 md:right-6 left-4 md:left-auto w-auto md:w-full max-w-[350px] md:max-w-[400px] h-[480px] md:h-[500px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden z-50 border border-slate-200">
          {/* Header */}
          <div className="bg-primary text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-secondary rounded-full flex items-center justify-center text-primary font-black">
                JE
              </div>
              <div>
                <h3 className="font-bold text-[14px]">Jetty Estates Assistant</h3>
                <p className="text-[10px] text-slate-300">We typically reply in minutes</p>
              </div>
            </div>
            <button onClick={() => setIsOpen(false)} className="text-white hover:text-secondary transition-colors">
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 bg-slate-50 space-y-4">
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex flex-col ${msg.sender === "user" ? "items-end" : "items-start"}`}>
                <div
                  className={`max-w-[80%] p-3 rounded-2xl text-[14px] ${
                    msg.sender === "user"
                      ? "bg-secondary text-primary font-medium rounded-br-none"
                      : "bg-white text-slate-800 border border-slate-200 rounded-bl-none shadow-sm"
                  }`}
                >
                  {msg.text}
                </div>
                {msg.options && msg.options.length > 0 && (
                  <div className="mt-2 flex flex-col gap-2 w-full max-w-[80%]">
                    {msg.options.map((opt, i) => (
                      <button
                        key={i}
                        onClick={() => handleOptionClick(opt)}
                        className="bg-white border border-primary text-primary text-[12px] py-2 px-4 rounded-full hover:bg-primary hover:text-white transition-colors text-left"
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {isTyping && (
              <div className="flex items-start">
                <div className="bg-white border border-slate-200 text-slate-500 p-3 rounded-2xl rounded-bl-none shadow-sm flex gap-1">
                  <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce"></span>
                  <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "0.2s" }}></span>
                  <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "0.4s" }}></span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="p-4 bg-white border-t border-slate-100 flex items-center gap-2">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleSend()}
              placeholder={step >= 6 ? "Chat ended" : "Type your message..."}
              disabled={step >= 6}
              className="flex-1 bg-slate-50 border-none outline-none p-3 rounded-xl text-[14px]"
            />
            <button
              onClick={handleSend}
              disabled={!inputValue.trim() || step >= 6}
              className="w-12 h-12 bg-primary text-white rounded-xl flex items-center justify-center hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faPaperPlane} />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatBot;
